import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import WineCardB2C from "../components/WineCardB2C";
import VendorProductCard from "../components/VendorProductCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { X, Wine } from "lucide-react";
import { useLang } from "../contexts/LangContext";

export default function Vini() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [wines, setWines] = useState([]);
  const [vendorWines, setVendorWines] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [winery, setWinery] = useState(params.get("marchio") || "");
  const [country, setCountry] = useState(params.get("paese") || "");
  const [color, setColor] = useState(params.get("tipologia") || "");
  const [vintage, setVintage] = useState(params.get("annata") || "");
  const [minPrice, setMinPrice] = useState(params.get("min") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max") || "");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/wines?limit=200").then((r) => setWines(r.data || []));
    api.get("/featured/wines?limit=4").then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/vendor/b2c/products?category=Vino&limit=200").then((r) => setVendorWines(r.data.products || [])).catch(() => {});
  }, []);

  // Vendor-uploaded wines (from company panels), filtered by search + price
  const filteredVendor = useMemo(() => {
    return vendorWines.filter((p) => {
      if (winery && winery !== (p.store_name || p.seller_company)) return false;
      if (minPrice && p.price < Number(minPrice)) return false;
      if (maxPrice && p.price > Number(maxPrice)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!(p.name || "").toLowerCase().includes(s) && !(p.description || "").toLowerCase().includes(s) && !((p.store_name || "")).toLowerCase().includes(s)) return false;
      }
      // hide vendor wines when a wine-specific filter (country/color/vintage) is set
      if (country || color || vintage) return false;
      return true;
    });
  }, [vendorWines, winery, country, color, vintage, minPrice, maxPrice, q]);

  // Distinct option lists
  const wineries = useMemo(() => Array.from(new Set([
    ...wines.map((w) => w.winery),
    ...vendorWines.map((v) => v.store_name || v.seller_company),
  ].filter(Boolean))).sort(), [wines, vendorWines]);
  const countries = useMemo(() => Array.from(new Set(wines.map((w) => w.country).filter(Boolean))).sort(), [wines]);
  const vintages = useMemo(() => Array.from(new Set(wines.map((w) => String(w.vintage)))).sort((a, b) => b.localeCompare(a)), [wines]);
  const colors = useMemo(() => Array.from(new Set(wines.map((w) => w.color))).filter(Boolean).sort(), [wines]);

  const filtered = useMemo(() => {
    return wines.filter((w) => {
      if (winery && w.winery !== winery) return false;
      if (country && w.country !== country) return false;
      if (color && w.color !== color) return false;
      if (vintage && String(w.vintage) !== vintage) return false;
      if (minPrice && w.price_per_bottle < Number(minPrice)) return false;
      if (maxPrice && w.price_per_bottle > Number(maxPrice)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!w.name.toLowerCase().includes(s) && !w.winery.toLowerCase().includes(s) && !(w.grape || "").toLowerCase().includes(s) && !(w.region || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [wines, winery, country, color, vintage, minPrice, maxPrice, q]);

  // Sync filters to URL (shareable)
  useEffect(() => {
    const next = {};
    if (winery) next.marchio = winery;
    if (country) next.paese = country;
    if (color) next.tipologia = color;
    if (vintage) next.annata = vintage;
    if (minPrice) next.min = minPrice;
    if (maxPrice) next.max = maxPrice;
    setParams(next, { replace: true });
    // eslint-disable-next-line
  }, [winery, country, color, vintage, minPrice, maxPrice]);

  const reset = () => { setWinery(""); setCountry(""); setColor(""); setVintage(""); setMinPrice(""); setMaxPrice(""); setQ(""); };
  const hasFilter = winery || country || color || vintage || minPrice || maxPrice || q;

  return (
    <div data-testid="vini-page">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[320px] max-h-[480px] overflow-hidden bg-[#1a0608]">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/heroes/vini_hero.png`} alt="" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="absolute inset-y-0 left-0 w-full md:w-3/5 bg-gradient-to-r from-black/45 to-transparent" />
        <div className="relative container-xl h-full flex flex-col justify-end pb-8 md:pb-12 text-white">
          <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#E2A03F] font-semibold mb-3 flex items-center gap-2 animate-fade-in-up">
            <Wine className="h-3.5 w-3.5" /> {t("vini.eyebrow")}
          </div>
          <h1 className="editorial-heading text-4xl sm:text-5xl md:text-6xl leading-tight md:leading-none mb-3 animate-fade-in-up animation-delay-150">{t("vini.title_pre")} <span className="italic text-[#E2A03F]">Amazonico 3000</span></h1>
          <p className="text-white/85 text-base md:text-lg max-w-2xl animate-fade-in-up animation-delay-300">
            {t("vini.subtitle").replace("{n}", wines.length || "1.200+")}
          </p>
        </div>
      </section>

      <div className="container-xl py-8 md:py-12">

      {/* FEATURED STRIP */}
      {wines.length > 0 && !hasFilter && (
        <div data-testid="vini-featured" className="mt-10 md:mt-12 mb-12 md:mb-16 bg-gradient-to-br from-[hsl(var(--primary))]/5 to-transparent border border-[hsl(var(--primary))]/15 rounded-sm p-5 md:p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold mb-3 flex items-center gap-2">
            <Wine className="h-3.5 w-3.5" /> {t("vini.bestseller")}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {(featured.length ? featured : wines.slice(0, 4)).map((w) => (<WineCardB2C key={w.id} wine={w} />))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div data-testid="vini-filters" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-country"><SelectValue placeholder={t("vini.ph_country")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_countries")}</SelectItem>
            {countries.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={winery || "all"} onValueChange={(v) => setWinery(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-winery"><SelectValue placeholder={t("vini.ph_winery")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_wineries")}</SelectItem>
            {wineries.map((w) => (<SelectItem key={w} value={w}>{w}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={color || "all"} onValueChange={(v) => setColor(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-color"><SelectValue placeholder={t("vini.ph_type")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_types")}</SelectItem>
            {colors.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>

        <Select value={vintage || "all"} onValueChange={(v) => setVintage(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-vintage"><SelectValue placeholder={t("vini.ph_vintage")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vini.all_vintages")}</SelectItem>
            {vintages.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
          </SelectContent>
        </Select>

        <div className="grid grid-cols-2 gap-2 sm:col-span-2 lg:col-span-2">
          <Input data-testid="filter-min-price" type="number" min={0} placeholder={t("vini.ph_min")} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
          <Input data-testid="filter-max-price" type="number" min={0} placeholder={t("vini.ph_max")} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Input data-testid="filter-search" placeholder={t("vini.ph_search")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
        {hasFilter && (
          <Button data-testid="filter-reset" variant="ghost" size="sm" onClick={reset} className="gap-1 text-muted-foreground">
            <X className="h-3 w-3" /> Reset filtri
          </Button>
        )}
        <div className="ml-auto text-sm text-muted-foreground">{filtered.length + filteredVendor.length} etichette</div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {filteredVendor.map((p) => (<VendorProductCard key={`v-${p.id}`} product={p} showStore />))}
        {filtered.map((w) => (<WineCardB2C key={w.id} wine={w} />))}
      </div>
      {filtered.length === 0 && filteredVendor.length === 0 && (
        <div data-testid="vini-empty" className="mt-16 text-center text-muted-foreground">
          Nessun vino corrisponde ai filtri selezionati.
        </div>
      )}
      </div>
    </div>
  );
}
